import { useEffect, useMemo, useState } from "react";
import { t } from "../locale";
import { ApiError } from "../../lib/api";
import { fetchBookingPage, type BookingPageData } from "../../lib/slottingApi";
import { parseDateOnlyUtc, dateKeyOf } from "../lib/offering-date";
import { useEventTimezone } from "../theme/WBThemeRuntimeProvider";

export function SlottingAvailabilityCalendar({
  offeringId,
  theme,
  // Editor-side default is `true` (SlottingAvailabilityWidget), so an
  // absent prop must keep rendering the legend — only an explicit opt-out
  // hides it. The editor honoured this all along; the runtime ignored it.
  showLegend = true,
}: {
  offeringId?: string;
  theme?: 'light' | 'dark';
  showLegend?: boolean;
}) {
  const [data, setData] = useState<BookingPageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const eventTz = useEventTimezone();

  useEffect(() => {
    let active = true;
    if (!offeringId) { setLoading(false); return; }
    fetchBookingPage(offeringId)
      .then((d) => { if (!active) return; setData(d); })
      .catch((err) => { if (!active) return; setError(err instanceof ApiError ? err.message : t("slottingFailedToLoad")); })
      .finally(() => { if (!active) return; setLoading(false); });
    return () => { active = false; };
  }, [offeringId]);

  // Build per-day availability map. Days are calendar days IN THE OFFERING
  // TIMEZONE (audit 2026-09-23, 4d): bucketing by the UTC prefix and walking
  // the range with `setDate` in UTC lit up the day BEFORE a morning slot east
  // of UTC and left the real day grey.
  const dayAvailability = useMemo(() => {
    if (!data) return new Map<string, "available" | "full" | "none">();
    const tz = data.offering.timezone || eventTz;
    const slotsByDay = new Map<string, number>();
    for (const slot of data.availableSlots) {
      const day = dateKeyOf(slot.startDt, tz);
      slotsByDay.set(day, (slotsByDay.get(day) ?? 0) + slot.spotsLeft);
    }
    const result = new Map<string, "available" | "full" | "none">();
    // Every offering-zone day the range touches: sample the range every 12 h
    // (a DST shift moves local time by ±1 h, so 12 h can never skip a local
    // day) and dedupe through the Set — insertion order stays chronological.
    if (data.offering.startDt && data.offering.endDt) {
      const startMs = Date.parse(data.offering.startDt);
      const endMs = Date.parse(data.offering.endDt);
      const days = new Set<string>();
      if (Number.isFinite(startMs) && Number.isFinite(endMs)) {
        for (let t = startMs; t <= endMs; t += 12 * 60 * 60 * 1000) {
          days.add(dateKeyOf(new Date(t).toISOString(), tz));
        }
        days.add(dateKeyOf(new Date(endMs).toISOString(), tz));
      }
      for (const dayStr of days) {
        const spots = slotsByDay.get(dayStr);
        result.set(dayStr, spots != null && spots > 0 ? "available" : spots != null ? "full" : "none");
      }
    }
    return result;
  }, [data, eventTz]);

  const DOT_COLORS = {
    available: "#22c55e",
    full: "#ef4444",
    none: "#d1d5db",
  };

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: "24px 0" }}>
        <div style={{ width: 16, height: 16, border: "2px solid #ddd", borderTopColor: "#121212", borderRadius: "50%", animation: "spin 1s linear infinite" }} />
      </div>
    );
  }

  if (error || !data || dayAvailability.size === 0) {
    return <div style={{ textAlign: "center", padding: "24px", color: "#64748b", fontSize: 14 }}>{error || t("slottingNoAvailabilityData")}</div>;
  }

  // Group days by week for a calendar grid
  const days = Array.from(dayAvailability.entries()).sort(([a], [b]) => a.localeCompare(b));
  const firstEntry = days[0];
  if (!firstEntry) return null;
  const firstDay = parseDateOnlyUtc(firstEntry[0]);
  const startDow = (firstDay.getUTCDay() + 6) % 7; // 0=Mon
  const dayLabels = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

  // Pad start
  const cells: (typeof days[0] | null)[] = Array(startDow).fill(null);
  cells.push(...days);

  // Chunk into weeks
  const weeks: (typeof cells)[] = [];
  for (let i = 0; i < cells.length; i += 7) {
    weeks.push(cells.slice(i, i + 7));
  }

  return (
    <div className={theme === 'dark' ? 'dark' : ''}>
      {/* Day headers */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4, marginBottom: 4 }}>
        {dayLabels.map((d) => (
          <div key={d} style={{ textAlign: "center", fontSize: 11, color: "#94a3b8", fontWeight: 500, padding: 4 }}>{d}</div>
        ))}
      </div>

      {/* Calendar grid */}
      {weeks.map((week, wi) => (
        <div key={wi} style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4 }}>
          {week.map((cell, ci) => {
            if (!cell) return <div key={ci} />;
            const [dayStr, status] = cell;
            const dayNum = parseDateOnlyUtc(dayStr).getUTCDate();
            return (
              <div key={ci} style={{ textAlign: "center", padding: "6px 0" }}>
                <div style={{ fontSize: 12, marginBottom: 2 }}>{dayNum}</div>
                <div style={{ width: 6, height: 6, borderRadius: "50%", background: DOT_COLORS[status], margin: "0 auto" }} />
              </div>
            );
          })}
        </div>
      ))}

      {/* Legend */}
      {showLegend ? (
        <div style={{ display: "flex", gap: 16, marginTop: 12, justifyContent: "center" }}>
          {(["available", "full", "none"] as const).map((s) => (
            <div key={s} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, color: "#64748b" }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: DOT_COLORS[s] }} />
              {s === "available" ? t("slottingAvailable") : s === "full" ? t("slottingFull") : t("slottingNone")}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
